import { type ReactNode, useState } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { CustomerActivityMeta, CustomerCard } from "@acto/taskpane/field-sales-2/components/primitives/CustomerCard";
import { CustomerActionsSheetHost } from "@acto/taskpane/field-sales-2/components/patterns/TopBar";
import { CreateTodoFlowProvider } from "@acto/taskpane/field-sales-2/components/patterns/CreateTodoSheet";
import { useInfiniteScroll } from "@acto/taskpane/field-sales-2/pages/useInfiniteScroll";
import { CustomerSectionSkeleton } from "./CustomerSectionSkeleton";

const SKELETON_COUNT = 5;

const defaultActivityMeta = (customer: Customer) => (
  <CustomerActivityMeta
    customer={customer}
    lastOrderAt={customer.lastOrder?.date ? new Date(customer.lastOrder.date) : null}
  />
);

const Sentinel = styled("div")({ width: "100%", height: 1 });

const List = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `0 ${theme.fieldSalesTwo.spacing[12]} ${theme.fieldSalesTwo.spacing[32]}`,
  flex: 1,
}));

const EmptyState = styled("div")(({ theme }) => ({
  padding: `${theme.fieldSalesTwo.spacing[32]} ${theme.fieldSalesTwo.spacing[16]}`,
  textAlign: "center",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

interface CustomerSectionProps {
  customers: Customer[] | undefined;
  /** Backend-resolved KPI window for the cards' revenue period label. */
  revenuePeriod?: { start: Date; end: Date } | null;
  isLoading: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  onOpenCustomer: (customer: Customer) => void;
  /** Per-card bottom button (label + action); omitted, the cards render none. */
  primaryAction?: (customer: Customer) => { label: ReactNode; onClick: () => void };
  headerMeta?: (customer: Customer) => ReactNode;
  /** Per-card activity slot. Defaults to the last-visit/last-order columns; `null` renders none. */
  activityMeta?: ((customer: Customer) => ReactNode) | null;
  /** Empty-state copy; defaults to the focus list's. */
  emptyText?: string;
  /** Skeleton cards shown while loading. Defaults to 5; set lower for capped sections. */
  skeletonCount?: number;
}

export function CustomerSection({
  customers,
  revenuePeriod = null,
  isLoading,
  isLoadingMore = false,
  hasMore = false,
  loadMore,
  onOpenCustomer,
  primaryAction,
  headerMeta,
  activityMeta = defaultActivityMeta,
  emptyText,
  skeletonCount = SKELETON_COUNT,
}: CustomerSectionProps) {
  const t = useTranslations("fieldSalesTwo");

  // One actions sheet serves the whole list, retargeted to the tapped customer.
  // `activeCustomer` lingers after close so the sheet's close animation plays out.
  const [activeCustomer, setActiveCustomer] = useState<Customer | null>(null);
  const [actionsOpen, setActionsOpen] = useState(false);

  const openActions = (customer: Customer) => {
    setActiveCustomer(customer);
    setActionsOpen(true);
  };

  // Auto-load the next page as the list end scrolls into view (matches OrdersOverview). Inert on
  // sections that don't paginate (Home's capped focus list passes no loadMore / hasMore=false).
  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    hasMore,
    isLoadingMore,
    loadMore: loadMore ?? (() => undefined),
    resetKey: customers?.length ?? 0,
  });

  const showSkeletons = isLoading && (customers == null || customers.length === 0);
  const showEmpty = !isLoading && customers != null && customers.length === 0;

  return (
    <CreateTodoFlowProvider clientId={activeCustomer?.id}>
      <List>
        {showSkeletons ? (
          Array.from({ length: skeletonCount }).map((_, i) => (
            <CustomerSectionSkeleton key={i} withPrimaryAction={primaryAction != null} />
          ))
        ) : showEmpty ? (
          <EmptyState>{emptyText ?? t("home.empty")}</EmptyState>
        ) : (
          customers?.map((c) => (
            <CustomerCard
              key={c.id}
              customer={c}
              revenuePeriod={revenuePeriod}
              headerMeta={headerMeta?.(c)}
              activityMeta={activityMeta?.(c)}
              onOpenCustomer={() => onOpenCustomer(c)}
              onMoreActions={() => openActions(c)}
              primaryAction={primaryAction ? primaryAction(c) : undefined}
            />
          ))
        )}
        {/* Auto-loads the next page when this scrolls into view. Still rendered when a client-side
            chip filter empties the current page (matching clients may live on later pages), so the
            observer keeps paging until the server runs out. */}
        {hasMore && !showSkeletons && loadMore && <Sentinel ref={sentinelRef} aria-hidden="true" />}
      </List>
      {activeCustomer && (
        <CustomerActionsSheetHost
          clientId={activeCustomer.id}
          isOpen={actionsOpen}
          onOpenChange={setActionsOpen}
          customerName={activeCustomer.name}
        />
      )}
    </CreateTodoFlowProvider>
  );
}
